import { createSelector } from 'reselect';

import { EMAIL_DOMAIN_REGEX } from '../../constants/shipping';
import { ILinkSettingsGate } from '../../types/createSendLinkSettings';
import { ShippingModeEnum } from '../../types/createSendRecipients';
import { isLinkContentsDigital, mapLinkContents, TLinkContentItem } from '../../utils/createSendLink';
import {
  selectCsBoxId,
  selectCsIsBusinessReasonRequiredByCampaign,
  selectCsItems,
  selectCsKits,
} from '../createSend/selectors/contents';
import {
  selectCsAllowedDomains,
  selectCsIs2FaEnabled,
  selectCsIsDomainsEnabled,
  selectCsIsExpirationEnabled,
  selectCsIsOnHoldEnabled,
  selectCsIsPasswordEnabled,
  selectCsLinkExpirationDate,
  selectCsLinkOnHoldUntil,
  selectCsLinkPassword,
  selectCsRedemptionLimit,
  selectCsTwoFaRecipients,
} from '../createSend/selectors/linkSettings';
import { selectCreateSendMethod } from '../createSend/selectors/method';
import {
  selectCsClaimAddressIds,
  selectCsMultiKitAddressMode,
  selectCsOverrideAddress,
} from '../createSend/selectors/recipients';
import { selectCsBusinessReasonId } from '../createSend/selectors/sendSettings';

export const isValidDomain = (domain: string): boolean => new RegExp(EMAIL_DOMAIN_REGEX).test(domain);

export const selectIsRedemptionLimitEnabled = createSelector(selectCsRedemptionLimit, (limit) => limit !== null);

export const selectFirstInvalidDomain = createSelector(
  selectCsAllowedDomains,
  (domains) => (domains ?? []).find((domain) => !isValidDomain(domain)) ?? null,
);

export const selectIsLinkPayloadDigital = createSelector(
  selectCsKits,
  selectCsItems,
  selectCsBoxId,
  (kits, items, preCreatedBoxId): boolean =>
    isLinkContentsDigital(mapLinkContents(kits ?? [], (items ?? []) as TLinkContentItem[], preCreatedBoxId)),
);

export const selectIsLinkOnHoldApplicable = createSelector(
  selectCreateSendMethod,
  selectIsLinkPayloadDigital,
  (method, isDigital): boolean => method === 'link' && !isDigital,
);

export const selectLinkShippingGate = createSelector(
  selectCsMultiKitAddressMode,
  selectCsOverrideAddress,
  selectCsClaimAddressIds,
  selectIsLinkPayloadDigital,
  (mode, overrideAddress, claimAddressIds, isDigital): ILinkSettingsGate => {
    if (isDigital) {
      return { valid: true, reason: null };
    }
    if (mode === ShippingModeEnum.FixedAddress && !overrideAddress) {
      return { valid: false, reason: 'fixed-address-required' };
    }
    if (mode === ShippingModeEnum.FromList && claimAddressIds.length < 2) {
      return { valid: false, reason: 'from-list-needs-2' };
    }
    return { valid: true, reason: null };
  },
);

export const selectLinkLimitsGate = createSelector(
  selectCsIsExpirationEnabled,
  selectCsLinkExpirationDate,
  selectIsLinkOnHoldApplicable,
  selectCsIsOnHoldEnabled,
  selectCsLinkOnHoldUntil,
  (isExpirationEnabled, expirationDate, isOnHoldApplicable, isOnHoldEnabled, onHoldUntil): ILinkSettingsGate => {
    if (isExpirationEnabled && !expirationDate) {
      return { valid: false, reason: 'expiration-date-required' };
    }

    if (isOnHoldApplicable && isOnHoldEnabled && !onHoldUntil) {
      return { valid: false, reason: 'on-hold-date-required' };
    }

    return { valid: true, reason: null };
  },
);

export const selectLinkAdvancedGate = createSelector(
  selectCsIsPasswordEnabled,
  selectCsLinkPassword,
  selectCsIsDomainsEnabled,
  selectCsAllowedDomains,
  selectFirstInvalidDomain,
  selectCsIs2FaEnabled,
  selectCsTwoFaRecipients,
  (
    isPasswordEnabled,
    password,
    isDomainsEnabled,
    domains,
    invalidDomain,
    is2FaEnabled,
    twoFaRecipients,
  ): ILinkSettingsGate => {
    if (isPasswordEnabled && !(password ?? '').trim()) {
      return { valid: false, reason: 'password-required' };
    }

    if (isDomainsEnabled && !(domains ?? []).length) {
      return { valid: false, reason: 'domains-required' };
    }

    if (invalidDomain !== null) {
      return { valid: false, reason: 'invalid-domain' };
    }

    if (is2FaEnabled && !(twoFaRecipients?.successfulRecipients ?? []).length) {
      return { valid: false, reason: 'two-fa-recipients-required' };
    }

    return { valid: true, reason: null };
  },
);

export const selectLinkSettingsGate = createSelector(
  selectLinkLimitsGate,
  selectLinkAdvancedGate,
  selectLinkShippingGate,
  selectCsIsBusinessReasonRequiredByCampaign,
  selectCsBusinessReasonId,
  (limits, advanced, shipping, isBusinessReasonRequired, businessReasonId): ILinkSettingsGate => {
    if (!limits.valid) {
      return limits;
    }

    if (!advanced.valid) {
      return advanced;
    }

    if (!shipping.valid) {
      return shipping;
    }

    if (isBusinessReasonRequired && !businessReasonId) {
      return { valid: false, reason: 'business-reason-required' };
    }

    return { valid: true, reason: null };
  },
);
